import React, { useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { cn } from '@epoch-ui/lib/utils';
import { Icon } from '@epoch-ui/components/Icon';
import { SheetHeader } from '@epoch-ui/components/Sheet/sheet';
import { QUESTION_TYPE, Question } from 'types/question.types';
import {
  QUESTION_CONFIG,
  QuestionFormInput,
  QuestionFormValues
} from '@event-editor/components/EditQuestionSideSheet/config/questionConfig';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { DuplicateQuestionButton } from './buttons/DuplicateQuestionButton';
import { DeleteQuestionButton } from './buttons/DeleteQuestionButton';
import { SubmitQuestionButton } from './buttons/SubmitQuestionButton';

interface QuestionnaireQuestionEditorProps {
  type: QUESTION_TYPE;
  onBack: () => void;
  handleClose: () => void;
  question?: Question;
  disabled?: boolean;
}

export const QuestionnaireQuestionEditor: React.FC<QuestionnaireQuestionEditorProps> = ({
  type,
  onBack,
  handleClose,
  question,
  disabled
}) => {
  const event = useEditableEvent();

  // Get the configuration for this question type
  const {
    defaultValues,
    schema,
    component: QuestionComponent,
    convertValuesFromDB
  } = useMemo(() => {
    const conf = QUESTION_CONFIG[type];
    if (!conf) {
      throw new Error(`Unsupported question type: ${type}`);
    }
    return conf;
  }, [type]);

  // Get the initial values by merging defaults with input question
  const initialValues = useMemo<QuestionFormInput>(() => {
    // Start with default values
    let values = defaultValues;

    // If we have a question, merge it with defaults
    if (question) {
      values = {
        ...defaultValues,
        ...question,
        // Ensure type is set correctly
        type
      } as QuestionFormValues;
    }

    // Convert to form format if needed (even for default values)
    if (convertValuesFromDB) {
      return convertValuesFromDB(values);
    }

    return values;
  }, [defaultValues, convertValuesFromDB, question, type]);

  // Initialize the form
  const methods = useForm<QuestionFormInput, unknown, QuestionFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: initialValues
  });

  if (!event.id) {
    return null;
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      {!question && (
        <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-pb-0">
          <div
            data-testid="back-button"
            onClick={onBack}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-text-content-tertiary"
          >
            <Icon name="arrow-left" />
            <span className="tw-text-sm tw-font-normal tw-leading-5">Add question</span>
          </div>
        </SheetHeader>
      )}

      <FormProvider {...methods}>
        <div
          className={cn('tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-auto', {
            'tw-mt-4': !question
          })}
        >
          <QuestionComponent disabled={disabled} />
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-gap-3">
            <SubmitQuestionButton
              eventId={event.id}
              existingQuestion={question}
              onClose={handleClose}
              disabled={disabled}
            />

            {question && (
              <DuplicateQuestionButton
                eventId={event.id}
                existingQuestion={question}
                disabled={disabled}
              />
            )}
          </div>

          <DeleteQuestionButton
            eventId={event.id}
            existingQuestion={question}
            onClose={handleClose}
            disabled={disabled}
          />
        </div>
      </FormProvider>
    </div>
  );
};
